import { fetchReviewSummary } from '@theme/review-api';

/**
 * Custom element that displays a simple rating: black star icon + number (e.g. 4.5)
 */
class ReviewRating extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    const productId = this.dataset.productId;
    if (productId && productId !== 'PREVIEW-ID') {
      this.loadRating(productId);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  async loadRating(productId) {
    if (!productId || productId === 'PREVIEW-ID') return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const loadingIndicator = this.querySelector('[ref="loadingIndicator"]');
    const ratingValue = this.querySelector('[ref="ratingValue"]');

    if (loadingIndicator) {
      loadingIndicator.classList.remove('visually-hidden');
    }
    if (ratingValue) {
      ratingValue.classList.add('visually-hidden');
    }

    try {
      const summary = await fetchReviewSummary(productId);

      if (this.#abortController.signal.aborted) return;

      if (loadingIndicator) {
        loadingIndicator.classList.add('visually-hidden');
      }

      if (!summary || summary.totalReviews === 0) {
        this.classList.add('visually-hidden');
        return;
      }

      this.classList.remove('visually-hidden');
      const ratingEl = this.querySelector('[ref="ratingValue"]');
      if (ratingEl) {
        ratingEl.textContent = summary.averageRating.toFixed(1);
        ratingEl.classList.remove('visually-hidden');
        ratingEl.setAttribute('aria-label', `Rating: ${summary.averageRating.toFixed(1)} out of 5`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading review rating:', error);
      }
      if (loadingIndicator) {
        loadingIndicator.classList.add('visually-hidden');
      }
    }
  }
}

if (!customElements.get('review-rating')) {
  customElements.define('review-rating', ReviewRating);
}
